import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Check,
  Mail,
  MessageSquare,
  Printer,
  RefreshCw,
  Send,
  Sparkles,
  BarChart3,
  User,
  AlertTriangle,
} from 'lucide-react';
import type { PrideTone, PrideStatus } from 'shared';
import { api, ApiError } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton, SkeletonText } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { Sparkline, type SparklineDataPoint } from '@/components/checkup/Sparkline';
import { cn } from '@/lib/cn';

/* ==========================================================================
   S8 — Pride Preview & Send

   The customer-facing Pride document: AI-generated treatment proposal
   with tone selector, trend charts, approval, and multi-channel send.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface TreatmentRecommendation {
  name: string;
  price: number;
  duration: number;
  description?: string;
}

interface ProductRecommendation {
  name: string;
  price?: number;
  description?: string;
}

interface BundleOffer {
  name: string;
  originalPrice: number;
  discountedPrice: number;
  items: string[];
  validUntil?: string;
}

interface TrendData {
  label: string;
  unit: string;
  data: SparklineDataPoint[];
}

interface PrideContent {
  risultati: string;
  trend?: TrendData[];
  obiettivi: string;
  trattamenti: TreatmentRecommendation[];
  curaDomiciliare: ProductRecommendation[];
  propostaDedicata?: BundleOffer;
}

interface PrideRecord {
  id: number;
  sessionId: number;
  tone: PrideTone;
  status: PrideStatus;
  content: PrideContent;
  generationCount: number;
  maxGenerations: number;
  includeTrendChart: boolean;
  clientName: string;
  createdAt: string;
  approvedAt?: string;
  sentAt?: string;
  sentChannels?: string[];
}

type SendChannel = 'stampa' | 'email' | 'whatsapp';

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const TONE_OPTIONS: Array<{
  value: PrideTone;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
}> = [
  {
    value: 'aggressive',
    label: 'Vendita',
    icon: '🔥',
    color: 'text-status-negative',
    bgColor: 'bg-status-negative-light',
    borderColor: 'border-status-negative',
  },
  {
    value: 'balanced',
    label: 'Equilibrato',
    icon: '⚖️',
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary-light',
    borderColor: 'border-accent-primary',
  },
  {
    value: 'soft',
    label: 'Soft',
    icon: '🕊️',
    color: 'text-accent-secondary',
    bgColor: 'bg-accent-secondary-light',
    borderColor: 'border-accent-secondary',
  },
];

const CHANNEL_OPTIONS: Array<{
  value: SendChannel;
  label: string;
  icon: typeof Printer;
}> = [
  { value: 'stampa', label: 'Stampa', icon: Printer },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
];

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function PridePreview() {
  const { id, checkupId } = useParams<{ id?: string; checkupId?: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  // The session ID for the checkup
  const sessionId = checkupId ? parseInt(checkupId, 10) : undefined;
  // The pride ID if navigating directly
  const prideId = id ? parseInt(id, 10) : undefined;

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------

  const [pride, setPride] = useState<PrideRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Controls
  const [tone, setTone] = useState<PrideTone>('balanced');
  const [includeTrendChart, setIncludeTrendChart] = useState(true);
  const [selectedChannels, setSelectedChannels] = useState<Set<SendChannel>>(
    new Set(),
  );

  // ------------------------------------------------------------------
  // Fetch existing Pride or generate new
  // ------------------------------------------------------------------

  const loadPride = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let data: PrideRecord | null = null;

      if (prideId) {
        // Direct pride ID route
        data = await api.get<PrideRecord>(`/pride/${prideId}`);
      } else if (sessionId) {
        // Try to load existing pride for this session
        try {
          data = await api.get<PrideRecord>(`/pride/session/${sessionId}`);
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) {
            // No existing pride — will generate on first action
            data = null;
          } else {
            throw err;
          }
        }
      }

      if (data) {
        setPride(data);
        setTone(data.tone);
        setIncludeTrendChart(data.includeTrendChart);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.status === 404
            ? 'Pride non trovato.'
            : err.message,
        );
      } else {
        setError('Errore di connessione. Riprova.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [prideId, sessionId]);

  useEffect(() => {
    loadPride();
  }, [loadPride]);

  // ------------------------------------------------------------------
  // Generate Pride
  // ------------------------------------------------------------------

  const handleGenerate = useCallback(async () => {
    if (!sessionId && !prideId) return;
    setIsGenerating(true);
    setError(null);

    try {
      let data: PrideRecord;

      if (pride) {
        // Regenerate existing
        data = await api.post<PrideRecord>(
          `/pride/${pride.id}/regenerate`,
          { tone, includeTrendChart },
        );
        toast.success('Pride rigenerato con successo');
      } else {
        // First generation
        data = await api.post<PrideRecord>('/pride/generate', {
          sessionId,
          tone,
          includeTrendChart,
        });
        toast.success('Pride generato con successo');
      }

      setPride(data);
      setTone(data.tone);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          toast.error('Numero massimo di rigenerazioni raggiunto');
        } else {
          toast.error(err.message || 'Errore nella generazione del Pride');
        }
      } else {
        toast.error('Errore di connessione. Riprova.');
      }
    } finally {
      setIsGenerating(false);
    }
  }, [sessionId, prideId, pride, tone, includeTrendChart, toast]);

  // ------------------------------------------------------------------
  // Approve Pride
  // ------------------------------------------------------------------

  const handleApprove = useCallback(async () => {
    if (!pride) return;
    setIsApproving(true);

    try {
      const data = await api.post<PrideRecord>(
        `/pride/${pride.id}/approve`,
      );
      setPride(data);
      toast.success('Pride approvato');
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message || 'Errore nell\'approvazione');
      } else {
        toast.error('Errore di connessione. Riprova.');
      }
    } finally {
      setIsApproving(false);
    }
  }, [pride, toast]);

  // ------------------------------------------------------------------
  // Send Pride
  // ------------------------------------------------------------------

  const handleSend = useCallback(async () => {
    if (!pride || selectedChannels.size === 0) return;
    setIsSending(true);

    try {
      const data = await api.post<PrideRecord>(`/pride/${pride.id}/send`, {
        channels: Array.from(selectedChannels),
      });
      setPride(data);
      toast.success('Pride inviato con successo');
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message || 'Errore nell\'invio');
      } else {
        toast.error('Errore di connessione. Riprova.');
      }
    } finally {
      setIsSending(false);
    }
  }, [pride, selectedChannels, toast]);

  // ------------------------------------------------------------------
  // Channel toggle
  // ------------------------------------------------------------------

  const toggleChannel = useCallback((channel: SendChannel) => {
    setSelectedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) {
        next.delete(channel);
      } else {
        next.add(channel);
      }
      return next;
    });
  }, []);

  // ------------------------------------------------------------------
  // Derived state
  // ------------------------------------------------------------------

  const isApproved = pride?.status === 'approved' || pride?.status === 'sent';
  const isSent = pride?.status === 'sent';
  const canRegenerate =
    pride !== null &&
    !isApproved &&
    pride.generationCount < pride.maxGenerations;

  // ------------------------------------------------------------------
  // Loading skeleton
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <AppShell header={<Header showBack title="Pride" />}>
        <div className="space-y-6">
          {/* Tone selector skeleton */}
          <div className="flex gap-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={48} className="flex-1 rounded-[var(--radius-md)]" />
            ))}
          </div>
          {/* Toggle skeleton */}
          <Skeleton height={24} className="w-48" />
          {/* Pride card skeleton */}
          <div className="rounded-[var(--radius-xl)] border border-border-default bg-surface-card p-6 space-y-6">
            <Skeleton height={20} className="w-40" />
            <SkeletonText lines={3} />
            <Skeleton height={20} className="w-40" />
            <SkeletonText lines={2} />
            <Skeleton height={20} className="w-40" />
            <SkeletonText lines={3} />
          </div>
          {/* Action buttons skeleton */}
          <div className="flex gap-3">
            <Skeleton height={48} className="flex-1 rounded-[var(--radius-md)]" />
            <Skeleton height={48} className="flex-1 rounded-[var(--radius-md)]" />
          </div>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Error state (no pride yet)
  // ------------------------------------------------------------------

  if (error && !pride) {
    return (
      <AppShell header={<Header showBack title="Pride" />}>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div
            className={cn(
              'mb-5 flex h-16 w-16 items-center justify-center',
              'rounded-full bg-status-negative-light',
            )}
          >
            <AlertTriangle className="h-7 w-7 text-status-negative" strokeWidth={1.5} />
          </div>
          <p className="text-lg font-[family-name:var(--font-heading)] font-semibold text-text-primary">
            {error}
          </p>
          <p className="mt-2 text-sm text-text-muted">
            Verifica la connessione e riprova
          </p>
          <Button
            variant="secondary"
            size="md"
            className="mt-6"
            onClick={() => navigate(-1)}
          >
            Torna indietro
          </Button>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Empty state — no pride generated yet
  // ------------------------------------------------------------------

  if (!pride) {
    return (
      <AppShell header={<Header showBack title="Pride" />}>
        <div className="space-y-8">
          {/* Tone selector */}
          <ToneSelector value={tone} onChange={setTone} disabled={isGenerating} />

          {/* Trend toggle */}
          <TrendToggle
            value={includeTrendChart}
            onChange={setIncludeTrendChart}
            disabled={isGenerating}
          />

          {/* Empty state CTA */}
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div
              className={cn(
                'mb-5 flex h-20 w-20 items-center justify-center',
                'rounded-full bg-accent-primary-light',
              )}
            >
              <Sparkles className="h-9 w-9 text-accent-primary" strokeWidth={1.5} />
            </div>
            <h2 className="text-xl font-[family-name:var(--font-heading)] font-bold text-text-primary">
              Genera il Pride
            </h2>
            <p className="mt-2 text-sm text-text-muted max-w-sm">
              {"L'intelligenza artificiale analizzer\u00e0 i dati del check-up e creer\u00e0 una proposta personalizzata per la cliente."}
            </p>
            <Button
              size="lg"
              className="mt-8"
              leftIcon={<Sparkles strokeWidth={2} />}
              loading={isGenerating}
              onClick={handleGenerate}
            >
              Genera Pride
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Main render — Pride exists
  // ------------------------------------------------------------------

  return (
    <AppShell
      header={
        <Header
          showBack
          title="Pride"
          actions={
            pride.clientName ? (
              <Badge status="info" size="sm">
                <User className="h-3 w-3" />
                {pride.clientName}
              </Badge>
            ) : undefined
          }
        />
      }
    >
      <div className="space-y-6 pb-8">
        {/* Status badge */}
        {isSent && (
          <div className="flex justify-center">
            <Badge status="positive" size="md" dot>
              Inviato {pride.sentChannels?.join(', ')}
            </Badge>
          </div>
        )}

        {/* ---- Tone selector ---- */}
        {!isApproved && (
          <ToneSelector
            value={tone}
            onChange={setTone}
            disabled={isGenerating}
          />
        )}

        {/* ---- Trend toggle ---- */}
        {!isApproved && (
          <TrendToggle
            value={includeTrendChart}
            onChange={setIncludeTrendChart}
            disabled={isGenerating}
          />
        )}

        {/* ---- Pride Card ---- */}
        <PrideCard
          content={pride.content}
          includeTrendChart={pride.includeTrendChart}
          isGenerating={isGenerating}
        />

        {/* ---- Actions ---- */}
        {!isApproved && (
          <div className="flex gap-3">
            {/* Regenerate */}
            <Button
              variant="secondary"
              size="lg"
              className="flex-1"
              disabled={!canRegenerate || isGenerating}
              loading={isGenerating}
              leftIcon={<RefreshCw strokeWidth={2} />}
              onClick={handleGenerate}
            >
              Rigenera {pride.generationCount}/{pride.maxGenerations}
            </Button>

            {/* Approve */}
            <Button
              size="lg"
              className="flex-1"
              disabled={isGenerating}
              loading={isApproving}
              leftIcon={<Check strokeWidth={2} />}
              onClick={handleApprove}
            >
              Approva
            </Button>
          </div>
        )}

        {/* ---- Send section (after approval) ---- */}
        {isApproved && !isSent && (
          <div className="space-y-4">
            <h3 className="font-[family-name:var(--font-heading)] text-base font-semibold text-text-primary">
              Seleziona canali di invio
            </h3>

            <div className="flex gap-3">
              {CHANNEL_OPTIONS.map(({ value, label, icon: Icon }) => {
                const isSelected = selectedChannels.has(value);
                return (
                  <button
                    key={value}
                    onClick={() => toggleChannel(value)}
                    className={cn(
                      'flex flex-1 flex-col items-center gap-2',
                      'py-4 px-3 rounded-[var(--radius-lg)]',
                      'border-2 transition-all duration-[var(--duration-normal)]',
                      'cursor-pointer min-h-[var(--touch-comfortable)]',
                      isSelected
                        ? 'border-accent-primary bg-accent-primary-light'
                        : 'border-border-default bg-surface-card hover:border-border-hover',
                      'active:scale-[0.97]',
                    )}
                    aria-pressed={isSelected}
                    aria-label={`${label}: ${isSelected ? 'selezionato' : 'non selezionato'}`}
                  >
                    <Icon
                      className={cn(
                        'h-6 w-6',
                        isSelected
                          ? 'text-accent-primary'
                          : 'text-text-muted',
                      )}
                      strokeWidth={1.5}
                    />
                    <span
                      className={cn(
                        'text-sm font-[family-name:var(--font-heading)] font-semibold',
                        isSelected
                          ? 'text-accent-primary'
                          : 'text-text-secondary',
                      )}
                    >
                      {label}
                    </span>
                    {isSelected && (
                      <div className="h-2 w-2 rounded-full bg-accent-primary" />
                    )}
                  </button>
                );
              })}
            </div>

            <Button
              size="lg"
              fullWidth
              disabled={selectedChannels.size === 0}
              loading={isSending}
              leftIcon={<Send strokeWidth={2} />}
              onClick={handleSend}
            >
              Invia Pride
            </Button>
          </div>
        )}

        {/* ---- Post-send state ---- */}
        {isSent && (
          <div className="text-center space-y-4 py-4">
            <p className="text-sm text-text-muted">
              Pride inviato con successo.
            </p>
            <Button
              variant="secondary"
              size="md"
              onClick={() => navigate(-1)}
            >
              Torna al check-up
            </Button>
          </div>
        )}
      </div>
    </AppShell>
  );
}

/* ==========================================================================
   Sub-components
   ========================================================================== */

/* --------------------------------------------------------------------------
   Tone Selector
   -------------------------------------------------------------------------- */

function ToneSelector({
  value,
  onChange,
  disabled = false,
}: {
  value: PrideTone;
  onChange: (tone: PrideTone) => void;
  disabled?: boolean;
}) {
  return (
    <section>
      <h3 className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-secondary mb-3">
        Tono della proposta
      </h3>
      <div className="flex gap-2">
        {TONE_OPTIONS.map((opt) => {
          const isActive = value === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              disabled={disabled}
              className={cn(
                'flex-1 flex items-center justify-center gap-2',
                'py-3 px-3 rounded-[var(--radius-md)]',
                'border-2 transition-all duration-[var(--duration-normal)]',
                'font-[family-name:var(--font-heading)] text-sm font-semibold',
                'cursor-pointer min-h-[var(--touch-min)]',
                'active:scale-[0.97]',
                'disabled:opacity-50 disabled:pointer-events-none',
                isActive
                  ? `${opt.bgColor} ${opt.color} ${opt.borderColor}`
                  : 'bg-surface-card text-text-secondary border-border-default hover:border-border-hover',
              )}
              aria-pressed={isActive}
              aria-label={`Tono ${opt.label}`}
            >
              <span aria-hidden>{opt.icon}</span>
              {opt.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
   Trend Chart Toggle
   -------------------------------------------------------------------------- */

function TrendToggle({
  value,
  onChange,
  disabled = false,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex items-center gap-3 px-4 py-3',
        'rounded-[var(--radius-md)] bg-surface-card border border-border-default',
        'cursor-pointer select-none',
        'transition-all duration-[var(--duration-fast)]',
        'hover:border-border-hover',
        disabled && 'opacity-50 pointer-events-none',
      )}
    >
      <BarChart3
        className="h-5 w-5 text-accent-secondary shrink-0"
        strokeWidth={1.5}
      />
      <span className="flex-1 font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
        Includi grafico trend
      </span>
      {/* Custom switch */}
      <div
        className={cn(
          'relative h-6 w-11 rounded-full',
          'transition-colors duration-[var(--duration-normal)]',
          value ? 'bg-accent-primary' : 'bg-border-default',
        )}
      >
        <div
          className={cn(
            'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm',
            'transition-transform duration-[var(--duration-normal)]',
            value ? 'translate-x-[22px]' : 'translate-x-0.5',
          )}
        />
        <input
          type="checkbox"
          className="sr-only"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          aria-label="Includi grafico trend"
        />
      </div>
    </label>
  );
}

/* --------------------------------------------------------------------------
   Pride Card — the premium, customer-facing formatted content
   -------------------------------------------------------------------------- */

function PrideCard({
  content,
  includeTrendChart,
  isGenerating,
}: {
  content: PrideContent;
  includeTrendChart: boolean;
  isGenerating: boolean;
}) {
  if (isGenerating) {
    return (
      <div
        className={cn(
          'rounded-[var(--radius-xl)] border border-border-default',
          'bg-surface-card shadow-md overflow-hidden',
        )}
      >
        <div className="p-6 space-y-6">
          {/* Generating overlay */}
          <div className="flex flex-col items-center py-12 text-center">
            <div
              className={cn(
                'mb-4 flex h-16 w-16 items-center justify-center',
                'rounded-full bg-accent-primary-light',
                'animate-pulse',
              )}
            >
              <Sparkles className="h-8 w-8 text-accent-primary" strokeWidth={1.5} />
            </div>
            <p className="font-[family-name:var(--font-heading)] text-base font-semibold text-text-primary">
              Generazione in corso...
            </p>
            <p className="text-sm text-text-muted mt-1">
              L'AI sta elaborando la proposta personalizzata
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-[var(--radius-xl)] border border-border-default',
        'bg-surface-card shadow-md overflow-hidden',
      )}
    >
      {/* ---- I Tuoi Risultati ---- */}
      <PrideSection
        title="I TUOI RISULTATI"
        accentColor="terracotta"
      >
        <p className="text-sm leading-relaxed text-text-primary whitespace-pre-line">
          {content.risultati}
        </p>
      </PrideSection>

      {/* ---- Il Tuo Trend (optional) ---- */}
      {includeTrendChart && content.trend && content.trend.length > 0 && (
        <PrideSection
          title="IL TUO TREND"
          accentColor="sage"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {content.trend.map((t, i) => (
              <Sparkline
                key={i}
                data={t.data}
                label={t.label}
                unit={t.unit}
                showDelta
                color={
                  i % 2 === 0
                    ? 'var(--color-accent-primary)'
                    : 'var(--color-accent-secondary)'
                }
              />
            ))}
          </div>
        </PrideSection>
      )}

      {/* ---- Obiettivi Prossimo Ciclo ---- */}
      <PrideSection
        title="OBIETTIVI PROSSIMO CICLO"
        accentColor="terracotta"
      >
        <p className="text-sm leading-relaxed text-text-primary whitespace-pre-line">
          {content.obiettivi}
        </p>
      </PrideSection>

      {/* ---- Trattamenti Consigliati ---- */}
      {content.trattamenti.length > 0 && (
        <PrideSection
          title="TRATTAMENTI CONSIGLIATI"
          accentColor="sage"
        >
          <div className="space-y-3">
            {content.trattamenti.map((t, i) => (
              <div
                key={i}
                className={cn(
                  'flex items-center gap-3 px-4 py-3',
                  'rounded-[var(--radius-md)] bg-surface-sunken',
                  'border border-border-default',
                )}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
                    {t.name}
                  </p>
                  {t.description && (
                    <p className="text-xs text-text-muted mt-0.5 line-clamp-2">
                      {t.description}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="font-[family-name:var(--font-heading)] text-sm font-bold text-accent-primary">
                    {t.price.toFixed(2)}&nbsp;&euro;
                  </p>
                  <p className="text-xs text-text-muted">
                    {t.duration}&nbsp;min
                  </p>
                </div>
              </div>
            ))}
          </div>
        </PrideSection>
      )}

      {/* ---- Cura Domiciliare ---- */}
      {content.curaDomiciliare.length > 0 && (
        <PrideSection
          title="CURA DOMICILIARE"
          accentColor="terracotta"
        >
          <div className="space-y-2">
            {content.curaDomiciliare.map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-3 py-2"
              >
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center shrink-0',
                    'rounded-full bg-accent-primary-light',
                  )}
                >
                  <span className="text-xs font-bold text-accent-primary">
                    {i + 1}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary">
                    {p.name}
                  </p>
                  {p.description && (
                    <p className="text-xs text-text-muted mt-0.5">
                      {p.description}
                    </p>
                  )}
                </div>
                {p.price !== undefined && p.price > 0 && (
                  <span className="text-sm font-[family-name:var(--font-heading)] font-semibold text-text-secondary shrink-0">
                    {p.price.toFixed(2)}&nbsp;&euro;
                  </span>
                )}
              </div>
            ))}
          </div>
        </PrideSection>
      )}

      {/* ---- Proposta Dedicata ---- */}
      {content.propostaDedicata && (
        <PrideBundleSection bundle={content.propostaDedicata} />
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Pride Section — reusable section with colored header accent
   -------------------------------------------------------------------------- */

function PrideSection({
  title,
  accentColor,
  children,
}: {
  title: string;
  accentColor: 'terracotta' | 'sage';
  children: ReactNode;
}) {
  return (
    <div className="border-b border-border-default last:border-b-0">
      {/* Section header with accent strip */}
      <div
        className={cn(
          'flex items-center gap-2 px-6 py-3',
          accentColor === 'terracotta'
            ? 'bg-accent-primary-light'
            : 'bg-accent-secondary-light',
        )}
      >
        <div
          className={cn(
            'h-1 w-4 rounded-full',
            accentColor === 'terracotta'
              ? 'bg-accent-primary'
              : 'bg-accent-secondary',
          )}
        />
        <h4
          className={cn(
            'font-[family-name:var(--font-heading)] text-xs font-bold tracking-wider',
            accentColor === 'terracotta'
              ? 'text-accent-primary'
              : 'text-accent-secondary',
          )}
        >
          {title}
        </h4>
      </div>

      {/* Section content */}
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Pride Bundle Section — special formatting for the bundled offer
   -------------------------------------------------------------------------- */

function PrideBundleSection({ bundle }: { bundle: BundleOffer }) {
  const savings = bundle.originalPrice - bundle.discountedPrice;
  const savingsPercent = Math.round(
    (savings / bundle.originalPrice) * 100,
  );

  return (
    <div className="bg-gradient-to-br from-accent-primary-light to-accent-secondary-light">
      {/* Header */}
      <div className="flex items-center gap-2 px-6 py-3 bg-accent-primary-medium">
        <Sparkles className="h-4 w-4 text-accent-primary" strokeWidth={2} />
        <h4 className="font-[family-name:var(--font-heading)] text-xs font-bold tracking-wider text-accent-primary">
          PROPOSTA DEDICATA
        </h4>
      </div>

      {/* Content */}
      <div className="px-6 py-5">
        <h5 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary">
          {bundle.name}
        </h5>

        {/* Items list */}
        <ul className="mt-3 space-y-1.5">
          {bundle.items.map((item, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-text-secondary">
              <Check className="h-4 w-4 text-status-positive shrink-0" strokeWidth={2} />
              {item}
            </li>
          ))}
        </ul>

        {/* Pricing */}
        <div className="mt-5 flex items-end gap-3">
          <span className="text-sm text-text-muted line-through">
            {bundle.originalPrice.toFixed(2)}&nbsp;&euro;
          </span>
          <span className="font-[family-name:var(--font-heading)] text-2xl font-extrabold text-accent-primary">
            {bundle.discountedPrice.toFixed(2)}&nbsp;&euro;
          </span>
          <Badge status="positive" size="sm">
            -{savingsPercent}%
          </Badge>
        </div>

        {bundle.validUntil && (
          <p className="mt-2 text-xs text-text-muted">
            Valida fino al{' '}
            {new Intl.DateTimeFormat('it-IT', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }).format(new Date(bundle.validUntil))}
          </p>
        )}
      </div>
    </div>
  );
}
